import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category ? category.name : article.category;

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.coverImage,
      datePublished: article.publishedAt,
      author: {
        '@type': 'Organization',
        name: '东升大舞台',
      },
      publisher: {
        '@type': 'Organization',
        name: '东升大舞台',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://dongshengdawutai.com/article/${article.slug}`,
      },
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 东升大舞台`}
        description={article.excerpt}
        keywords={`东升大舞台,${categoryName},${article.title}`}
        canonicalUrl={`https://dongshengdawutai.com/article/${article.slug}`}
        ogType="article"
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors"
              >
                {categoryName}
              </Link>
              <span className="text-xs text-foreground-500">{article.publishedAt}</span>
              <span className="text-xs text-foreground-500">&middot; 阅读约{article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              title={`${article.title} - 东升大舞台`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h4]:font-heading [&_h4]:text-lg [&_h4]:md:text-xl [&_h4]:font-bold [&_h4]:text-foreground-950 [&_h4]:mt-8 [&_h4]:mb-3
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-primary-600 [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200/70">
              <h4 className="font-heading text-lg font-bold text-foreground-950 mb-5">
                相关文章
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group cursor-pointer"
                  >
                    <div className="w-full h-36 rounded-lg overflow-hidden mb-3">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        title={`${ra.title} - 东升大舞台`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <h5 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h5>
                    <p className="text-xs text-foreground-500 mt-1">{ra.publishedAt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}